import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid, valueOrDefault } from '@web/utils';
import { AdminDictionaryCountryCollectionItemResponse } from '../responses/admin-dictionary-country-collection-item.response';
import { AdminDictionaryCountryDetailsResponse } from '../responses/admin-dictionary-country-details.response';
import { AdminDictionaryCountryCreateRequest } from '../requests/admin–dictionary-country-create.request';
import { AdminDictionaryCountryUpdateRequest } from '../requests/admin-dictionary-country-update.request';

@Injectable({
  providedIn: 'root',
})
export class AdminDictionaryCountryApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    request: CollectionRequest;
    cacheable?: boolean;
    skipByLoading?: boolean;
  }): Observable<
    CollectionResponse<AdminDictionaryCountryCollectionItemResponse>
  > {
    const path = 'admin/dictionary/country';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options.cacheable, false),
      )
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(options.skipByLoading, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminDictionaryCountryCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchDetailsById(
    id: Guid,
  ): Observable<AdminDictionaryCountryDetailsResponse> {
    const path = `admin/dictionary/country/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminDictionaryCountryDetailsResponse,
          ),
        ),
      );
  }

  public update(
    id: Guid,
    request: AdminDictionaryCountryUpdateRequest,
  ): Observable<void> {
    const path = `admin/dictionary/country/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .put(
        url,
        this.provider.httpHelper.requestToBody(
          request,
          AdminDictionaryCountryUpdateRequest,
        ),
        { context },
      )
      .pipe(blank());
  }

  public remove(id: Guid): Observable<void> {
    const path = `admin/dictionary/country/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    return this.provider.http.delete(url).pipe(blank());
  }

  public create(
    request: AdminDictionaryCountryCreateRequest,
  ): Observable<void> {
    const path = 'admin/dictionary/country';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminDictionaryCountryCreateRequest,
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
